import type { ReactNode } from "react";
import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import styles from "./ApplyCoaTemplatePreviewStep.module.scss";

interface Props {
    label: string;
    ariaChecked: boolean | "mixed";
    isDisabled: boolean;
    // Why the control can't be used. It joins the accessible name and shows as a
    // tooltip, so an unselectable control is never only "dimmed".
    disabledReason?: string;
    onClick: () => void;
    children: ReactNode;
}

// Shared entity/client selection control. It is never natively disabled: that
// drops it out of the tab order, hiding the one element that can explain why the
// selection isn't available. `aria-disabled` plus a guarded click convey the
// state instead.
export const ApplyCoaPreviewSelectionCheckbox = ({
    label,
    ariaChecked,
    isDisabled,
    disabledReason,
    onClick,
    children,
}: Props) => {
    const checkbox = (
        <button
            type="button"
            role="checkbox"
            aria-checked={ariaChecked}
            aria-label={disabledReason ? `${label}. ${disabledReason}` : label}
            aria-disabled={isDisabled}
            data-disabled={isDisabled}
            className={classNames(styles.rowCheckbox, {
                [styles.rowCheckboxDisabled]: isDisabled,
            })}
            onClick={() => {
                if (isDisabled) {
                    return;
                }
                onClick();
            }}
        >
            {children}
        </button>
    );
    if (!disabledReason) {
        return checkbox;
    }
    return <Tooltip content={disabledReason}>{checkbox}</Tooltip>;
};
